/**
 * <s-card-gallery> — swipeable product-card image strip.
 * Children: [data-track] (scroll-snap row of slides), optional [data-prev]/[data-next] buttons and [data-dot] markers.
 * Arrows scroll one slide and wrap; dots mirror the visible slide. Smooth scrolling only when motion is allowed.
 */
class SCardGallery extends HTMLElement {
  #ac;
  #raf = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.track = this.querySelector('[data-track]');
    if (!this.track) return;
    this.dots = [...this.querySelectorAll('[data-dot]')];

    this.querySelector('[data-prev]')?.addEventListener('click', (event) => this.#step(event, -1), opts);
    this.querySelector('[data-next]')?.addEventListener('click', (event) => this.#step(event, 1), opts);
    this.track.addEventListener('scroll', () => {
      cancelAnimationFrame(this.#raf);
      this.#raf = requestAnimationFrame(() => this.#sync());
    }, { ...opts, passive: true });
    this.#sync();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#raf);
  }

  get #index() {
    const width = this.track.clientWidth || 1;
    return Math.round(Math.abs(this.track.scrollLeft) / width);
  }

  #step(event, delta) {
    event.preventDefault();
    event.stopPropagation();
    const count = this.track.children.length;
    if (count < 2) return;
    const next = (this.#index + delta + count) % count;
    const rtl = getComputedStyle(this.track).direction === 'rtl';
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollTo({ left: next * this.track.clientWidth * (rtl ? -1 : 1), behavior: reduce ? 'auto' : 'smooth' });
  }

  #sync() {
    const index = this.#index;
    this.dots.forEach((dot, i) => dot.toggleAttribute('data-active', i === index));
  }
}

if (!customElements.get('s-card-gallery')) customElements.define('s-card-gallery', SCardGallery);
